import {
  Activity,
  AlertTriangle,
  Brain,
  CheckCircle2,
  ClipboardCheck,
  Gauge,
  Mic,
  Pause,
  Play,
  RefreshCw,
  Save,
  Square,
  Timer,
  Users
} from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  EFFORT_LABELS,
  MODEL_LIMITS,
  activeOnly,
  defaultUsageSnapshot,
  getModel,
  recommend,
  type ActiveSession,
  type Recommendation,
  type UsageSnapshot
} from "../shared/decision";

type PortalState = {
  usage: UsageSnapshot;
  sessions: ActiveSession[];
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onresult:
    | ((event: {
        resultIndex: number;
        results: ArrayLike<{
          isFinal: boolean;
          0: { transcript: string };
        }>;
      }) => void)
    | null;
};

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  }
}

const emptySignals = {
  urgent: false,
  highValue: false,
  productionRisk: false,
  parallelizable: false,
  needsPolish: false
};

export default function App() {
  const [state, setState] = useState<PortalState>({ usage: defaultUsageSnapshot(), sessions: [] });
  const [usageDraft, setUsageDraft] = useState<UsageSnapshot>(defaultUsageSnapshot());
  const [userName, setUserName] = useState(() => localStorage.getItem("model-gate-name") ?? "");
  const [portalKey, setPortalKey] = useState(() => localStorage.getItem("model-gate-key") ?? "");
  const [task, setTask] = useState("");
  const [signals, setSignals] = useState(emptySignals);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [dictating, setDictating] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  useEffect(() => {
    void refreshState();
  }, []);

  async function refreshState() {
    try {
      const next = await fetchJson<PortalState>("/api/state");
      next.sessions = activeOnly(next.sessions);
      setState(next);
      setUsageDraft(next.usage);
    } catch {
      const fallback = { usage: defaultUsageSnapshot(), sessions: [] };
      setState(fallback);
      setUsageDraft(fallback.usage);
    }
  }

  async function saveUsage(event: FormEvent) {
    event.preventDefault();
    setError("");
    try {
      const next = await fetchJson<PortalState>("/api/state", {
        method: "POST",
        headers: authHeaders(portalKey),
        body: JSON.stringify({ ...usageDraft, updatedBy: userName || "unknown" })
      });
      setState({ ...next, sessions: activeOnly(next.sessions) });
      localStorage.setItem("model-gate-name", userName);
      if (portalKey) localStorage.setItem("model-gate-key", portalKey);
    } catch (requestError) {
      setError(messageFor(requestError));
    }
  }

  async function assess(event: FormEvent) {
    event.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      localStorage.setItem("model-gate-name", userName);
      if (portalKey) localStorage.setItem("model-gate-key", portalKey);
      const local = recommend({ task, usage: usageDraft, activeSessions: state.sessions, signals });
      setRecommendation(local);
      const remote = await fetchJson<Recommendation>("/api/assess", {
        method: "POST",
        body: JSON.stringify({ task, usage: usageDraft, activeSessions: state.sessions, signals })
      });
      setRecommendation(remote);
    } catch (requestError) {
      setError(messageFor(requestError));
    } finally {
      setIsLoading(false);
    }
  }

  async function reserve() {
    if (!recommendation) return;
    setError("");
    try {
      const payload = {
        userName: userName || "Someone",
        task,
        recommendation,
        durationMinutes: recommendation.estimatedDurationMinutes
      };
      const result = await fetchJson<{ state: PortalState }>("/api/sessions", {
        method: "POST",
        headers: authHeaders(portalKey),
        body: JSON.stringify(payload)
      });
      setState({ ...result.state, sessions: activeOnly(result.state.sessions) });
    } catch (requestError) {
      setError(messageFor(requestError));
    }
  }

  async function releaseSession(id: string) {
    setError("");
    try {
      const next = await fetchJson<PortalState>(`/api/sessions/${id}`, {
        method: "DELETE",
        headers: authHeaders(portalKey)
      });
      setState({ ...next, sessions: activeOnly(next.sessions) });
    } catch (requestError) {
      setError(messageFor(requestError));
    }
  }

  function toggleDictation() {
    const SpeechRecognition = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("Voice dictation is not available in this browser.");
      return;
    }
    if (dictating) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onstart = () => setDictating(true);
    recognition.onend = () => setDictating(false);
    recognition.onerror = (event) => {
      setError(`Voice dictation stopped: ${event.error}`);
      setDictating(false);
    };
    recognition.onresult = (event) => {
      let finalText = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result.isFinal) finalText += result[0].transcript;
      }
      if (finalText.trim()) {
        setTask((current) => `${current}${current.trim() ? " " : ""}${finalText.trim()}`);
      }
    };
    recognitionRef.current = recognition;
    recognition.start();
  }

  const activeSessions = activeOnly(state.sessions);
  const visibleRecommendation = recommendation ?? recommend({ task, usage: usageDraft, activeSessions, signals });
  const selectedModel = getModel(visibleRecommendation.modelId);

  return (
    <main className="shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Shared Codex account</p>
          <h1>Model usage gate</h1>
        </div>
        <div className="topbar__status" aria-live="polite">
          <Gauge size={18} />
          <span>{visibleRecommendation.effectiveAvailablePercent}% usable</span>
        </div>
      </header>

      {error ? (
        <div className="error" role="alert">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      ) : null}

      <section className="layout" aria-label="Usage recommendation workspace">
        <form className="panel intake" onSubmit={assess}>
          <div className="panel__heading">
            <Brain size={19} />
            <h2>Work request</h2>
          </div>

          <label>
            Name
            <input
              value={userName}
              onChange={(event) => setUserName(event.target.value)}
              placeholder="Sahasta"
              autoComplete="name"
            />
          </label>

          <label>
            Task
            <textarea
              value={task}
              onChange={(event) => setTask(event.target.value)}
              placeholder="I need to build, debug, research, or review..."
              rows={9}
            />
          </label>

          <div className="dictation-row">
            <button type="button" className="icon-button" onClick={toggleDictation} aria-label={dictating ? "Stop dictation" : "Start dictation"}>
              {dictating ? <Square size={18} /> : <Mic size={18} />}
            </button>
            <span className={dictating ? "dictation-state is-live" : "dictation-state"}>
              {dictating ? "Listening" : "Voice"}
            </span>
          </div>

          <fieldset className="signals">
            <legend>Signals</legend>
            {Object.entries({
              urgent: "Urgent",
              highValue: "High value",
              productionRisk: "Prod risk",
              parallelizable: "Parallel",
              needsPolish: "Polish"
            }).map(([key, label]) => (
              <label className="toggle" key={key}>
                <input
                  type="checkbox"
                  checked={signals[key as keyof typeof signals]}
                  onChange={(event) => setSignals((current) => ({ ...current, [key]: event.target.checked }))}
                />
                <span>{label}</span>
              </label>
            ))}
          </fieldset>

          <button className="primary" type="submit" disabled={isLoading || !task.trim()}>
            <Play size={18} />
            <span>{isLoading ? "Assessing" : "Assess task"}</span>
          </button>
        </form>

        <section className={`panel clearance clearance--${visibleRecommendation.decision}`} aria-live="polite">
          <div className="panel__heading">
            {visibleRecommendation.decision === "wait" ? <Pause size={19} /> : <CheckCircle2 size={19} />}
            <h2>Clearance</h2>
          </div>

          <div className="clearance__verdict">
            <p>{visibleRecommendation.headline}</p>
            <span>{visibleRecommendation.assessor === "openai" ? "AI refined" : "Local policy"}</span>
          </div>

          <div className="dial" style={{ "--value": `${visibleRecommendation.effectiveAvailablePercent}%` } as React.CSSProperties}>
            <div>
              <strong>{visibleRecommendation.effectiveAvailablePercent}%</strong>
              <span>usable</span>
            </div>
          </div>

          <div className="model-strip">
            <Metric label="Model" value={selectedModel.shortLabel} />
            <Metric label="Reasoning" value={EFFORT_LABELS[visibleRecommendation.effort]} />
            <Metric label="Complexity" value={visibleRecommendation.complexityLabel} />
          </div>

          {visibleRecommendation.waitReason ? <p className="wait-reason">{visibleRecommendation.waitReason}</p> : null}

          <div className="rationale">
            {visibleRecommendation.rationale.slice(0, 4).map((item) => (
              <p key={item}>{item}</p>
            ))}
          </div>

          <div className="estimate-bar" aria-label="Estimated capacity use">
            <span style={{ width: `${visibleRecommendation.estimatedCapacityPercent}%` }} />
          </div>

          <div className="clearance__actions">
            <button type="button" onClick={reserve} disabled={!task.trim() || visibleRecommendation.decision === "wait"}>
              <ClipboardCheck size={18} />
              <span>Reserve</span>
            </button>
            <button type="button" onClick={() => setRecommendation(recommend({ task, usage: usageDraft, activeSessions, signals }))}>
              <RefreshCw size={18} />
              <span>Recompute</span>
            </button>
          </div>
        </section>

        <aside className="panel account">
          <div className="panel__heading">
            <Activity size={19} />
            <h2>Account pressure</h2>
          </div>

          <form onSubmit={saveUsage} className="usage-form">
            <label>
              Remaining
              <input
                type="range"
                min="0"
                max="100"
                value={usageDraft.remainingPercent}
                onChange={(event) =>
                  setUsageDraft((current) => ({ ...current, remainingPercent: Number(event.target.value) }))
                }
              />
              <output>{usageDraft.remainingPercent}%</output>
            </label>

            <label>
              Reserve
              <input
                type="range"
                min="0"
                max="80"
                value={usageDraft.reservePercent}
                onChange={(event) =>
                  setUsageDraft((current) => ({ ...current, reservePercent: Number(event.target.value) }))
                }
              />
              <output>{usageDraft.reservePercent}%</output>
            </label>

            <label>
              Active people
              <input
                type="number"
                min="0"
                max="20"
                value={usageDraft.activePeople}
                onChange={(event) => setUsageDraft((current) => ({ ...current, activePeople: Number(event.target.value) }))}
              />
            </label>

            <label>
              Source
              <select
                value={usageDraft.source}
                onChange={(event) => setUsageDraft((current) => ({ ...current, source: event.target.value as UsageSnapshot["source"] }))}
              >
                <option value="dashboard">Dashboard</option>
                <option value="cli-status">CLI status</option>
                <option value="estimate">Estimate</option>
              </select>
            </label>

            <label>
              Shared key
              <input
                type="password"
                value={portalKey}
                onChange={(event) => setPortalKey(event.target.value)}
                placeholder="optional"
                autoComplete="current-password"
              />
            </label>

            <button type="submit">
              <Save size={18} />
              <span>Save snapshot</span>
            </button>
          </form>

          <div className="session-list">
            <div className="session-list__heading">
              <Users size={18} />
              <span>{activeSessions.length} active</span>
            </div>
            {activeSessions.length === 0 ? (
              <p className="empty">No reservations.</p>
            ) : (
              activeSessions.map((session) => (
                <article className="session-card" key={session.id}>
                  <div>
                    <strong>{session.userName}</strong>
                    <span>{getModel(session.modelId).shortLabel} / {EFFORT_LABELS[session.effort]}</span>
                  </div>
                  <p>{session.task}</p>
                  <button type="button" onClick={() => void releaseSession(session.id)} aria-label={`Release ${session.userName}`}>
                    <Square size={15} />
                  </button>
                </article>
              ))
            )}
          </div>
        </aside>
      </section>

      <section className="limit-band" aria-label="Published limit reference">
        {MODEL_LIMITS.slice(0, 4).map((model) => (
          <div key={model.id} className="limit-item">
            <strong>{model.shortLabel}</strong>
            <span>{model.localMessages20x[0]}-{model.localMessages20x[1]} local messages / 5h</span>
          </div>
        ))}
        <a href="https://learn.chatgpt.com/docs/pricing" rel="noreferrer" target="_blank">
          Source
        </a>
      </section>

      <footer>
        <Timer size={16} />
        <span>
          Last snapshot: {state.usage.updatedAt ? new Date(state.usage.updatedAt).toLocaleString() : "not saved"}
        </span>
      </footer>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {})
    }
  });
  const data = (await response.json().catch(() => null)) as T & { error?: string };
  if (!response.ok) throw new Error(data?.error ?? `Request failed: ${response.status}`);
  return data as T;
}

function authHeaders(portalKey: string): HeadersInit {
  return portalKey ? { "x-portal-key": portalKey } : {};
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : "Request failed.";
}
